import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useHistory } from 'react-router-dom';
import { getProductGroupsPerPage, deleteProductGroup } from '../../../../requests/api/product-groups';
import Snackbar from '../../../../components/Snackbar';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import axios from '../../../../utils/axios';
import useStyles from './styles';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25, 50];
const TABLE_HEADERS = [
  { id: 'id', label: 'ID' },
  { id: 'name', label: 'Nombre' },
  { id: 'description', label: 'Descripción' },
  { id: 'actions', label: 'Acciones' },
];

const ProductGroupsTable = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  const history = useHistory();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [productGroups, setProductGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [message, setMessage] = useState('');
  const [companyId, setCompanyId] = useState(axios?.defaults?.params?.companyId);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const baseUrl = '/product-groups';

  // Company ID setup
  useEffect(() => {
    if (companyId) return;
    const interval = setInterval(() => {
      const currentId = axios?.defaults?.params?.companyId;
      if (currentId !== undefined) {
        setCompanyId(currentId);
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [companyId]);

  const handleError = useCallback(() => {
    setMessage(t('common.failedLoad'));
  }, [t]);

  // Action handlers
  const handleView = (id) => history.push(`${baseUrl}/${id}/detail`);
  const handleEdit = (id) => history.push(`${baseUrl}/${id}/edit`);
  const handleDelete = (id) => {
    const productGroup = productGroups.find((pg) => pg.id === id);
    setDeleteConfirm({
      open: true,
      id,
      name: productGroup?.name,
    });
  };

  const confirmDelete = async () => {
    const errorMessage = t('productGroup.deletedUnsuccessfully');
    try {
      const response = await deleteProductGroup(deleteConfirm.id);
      if (response.success) {
        setMessage(t('productGroup.deletedSuccessfully', { name: deleteConfirm.name }));
        const fetchResponse = await getProductGroupsPerPage(page, rowsPerPage, '', '');
        if (fetchResponse.success) {
          setProductGroups(fetchResponse.data.data.productGroups || []);
          setTotalCount(fetchResponse.data.data.totalProductGroups || 0);
        }
      } else {
        setMessage(errorMessage);
      }
    } catch (error) {
      setMessage(errorMessage);
    } finally {
      setDeleteConfirm({ open: false, id: null, name: '' });
    }
  };

  const cancelDelete = () => {
    setDeleteConfirm({ open: false, id: null, name: '' });
  };

  // Data fetching
  useEffect(() => {
    if (!companyId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await getProductGroupsPerPage(page, rowsPerPage, '', '');
        if (response.success) {
          setProductGroups(response.data.data.productGroups || []);
          setTotalCount(response.data.data.totalProductGroups || 0);
        } else {
          handleError();
        }
      } catch (error) {
        handleError();
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, page, rowsPerPage, handleError]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Render helpers
  const renderEmptyState = (message, colSpan = 3) => (
    <TableContainer>
      <Table>
        <TableBody>
          <TableRow>
            <TableCell colSpan={colSpan} style={{ textAlign: 'center', padding: '40px' }}>
              {loading ? (
                <>
                  <CircularProgress />
                  <div>{t('loading')}</div>
                </>
              ) : (
                <div>{message}</div>
              )}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderActionButtons = (rowId) => (
    <TableCell className={styles.iconsCell}>
      <Tooltip title="Ver">
        <IconButton onClick={() => handleView(rowId)}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Editar">
        <IconButton onClick={() => handleEdit(rowId)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Eliminar">
        <IconButton onClick={() => handleDelete(rowId)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </TableCell>
  );

  if (loading) return renderEmptyState(t('loading'));
  if (!productGroups.length) return renderEmptyState(t('productGroup.empty'));

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            {TABLE_HEADERS.map((header) => (
              <TableCell key={header.id} className={styles.tableHeader}>
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {productGroups.map((row) => (
            <TableRow key={row.id}>
              <TableCell>{row.id}</TableCell>
              <TableCell>{row.name || 'N/A'}</TableCell>
              <TableCell>{row.description || 'N/A'}</TableCell>
              {renderActionButtons(row.id)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage={t('common.rowsPerPage')}
        labelDisplayedRows={({ count }) => {
          const start = page * rowsPerPage + 1;
          const end = Math.min((page + 1) * rowsPerPage, count);
          return t('common.showingRecords', { from: start, to: end, total: count });
        }}
      />
      <Snackbar
        open={message !== ''}
        message={message}
        severity="error"
        onClose={() => setMessage('')}
      />
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('common.confirmDelete')}
        message={t('productGroup.deleteQuestion', { name: deleteConfirm.name })}
        description=""
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
      />
    </TableContainer>
  );
};

export default ProductGroupsTable;
